import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Mail, Phone, Globe, MapPin, Package, Search } from 'lucide-react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/suppliers/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const response = await axios.get(`${API}/suppliers`, { params });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter(supplier =>
    searchQuery === '' ||
    supplier.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
            <nav className="hidden md:flex gap-8 items-center">
              <Link to="/products" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Products
              </Link>
              <Link to="/suppliers" className="text-slate-900 font-medium">
                Suppliers
              </Link>
              <Link to="/subscriptions" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Subscriptions
              </Link>
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Home
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="suppliers-title">
            Raw Material Suppliers Directory
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl">
            Connect with verified global suppliers for all your manufacturing raw materials. From chemicals to packaging, find everything you need to start production.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search suppliers by name or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-lg border-slate-200 focus:border-slate-900"
              data-testid="supplier-search-input"
            />
          </div>
          <div className="sm:w-64">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="h-12 rounded-lg border-slate-200" data-testid="supplier-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          <div className="text-center py-12" data-testid="no-suppliers">
            <p className="text-slate-600 text-lg">No suppliers found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="suppliers-grid">
            {filteredSuppliers.map((supplier) => (
              <div
                key={supplier.id}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-xl transition-all"
                data-testid={`supplier-card-${supplier.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-sky-100 rounded-full p-2">
                    <Package className="h-5 w-5 text-sky-600" />
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full font-medium">
                    {supplier.category}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {supplier.company_name}
                </h3>

                <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                  {supplier.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{supplier.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <a href={`mailto:${supplier.contact_email}`} className="hover:text-sky-600 transition-colors">
                      {supplier.contact_email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <a href={`tel:${supplier.phone}`} className="hover:text-sky-600 transition-colors">
                      {supplier.phone}
                    </a>
                  </div>
                  {supplier.website && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Globe className="h-4 w-4 text-slate-400" />
                      <a
                        href={`https://${supplier.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-sky-600 transition-colors"
                      >
                        {supplier.website}
                      </a>
                    </div>
                  )}
                </div>

                {supplier.minimum_order && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-slate-600 mb-1">Minimum Order</p>
                    <p className="text-sm font-bold text-slate-900">{supplier.minimum_order}</p>
                  </div>
                )}

                <div className="mb-4">
                  <p className="text-xs text-slate-600 mb-2">Products Supplied:</p>
                  <div className="flex flex-wrap gap-2">
                    {supplier.products_supplied.slice(0, 3).map((product, idx) => (
                      <span key={idx} className="text-xs bg-sky-50 text-sky-700 px-2 py-1 rounded">
                        {product}
                      </span>
                    ))}
                    {supplier.products_supplied.length > 3 && (
                      <span className="text-xs text-slate-500">
                        +{supplier.products_supplied.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                <Button
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                  onClick={() => window.location.href = `mailto:${supplier.contact_email}`}
                  data-testid={`contact-supplier-${supplier.id}`}
                >
                  Contact Supplier
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Why Use Our Supplier Directory?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Verified Suppliers
              </h3>
              <p className="text-slate-600">
                All suppliers are verified and have proven track records in the industry.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Global Network
              </h3>
              <p className="text-slate-600">
                Access suppliers from around the world for competitive pricing and variety.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-sky-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-sky-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Complete Information
              </h3>
              <p className="text-slate-600">
                Get all the details you need including minimum orders and product ranges.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Suppliers;
